import { MouseEvent, MouseEventHandler, RefObject, useEffect, useRef, useState } from "react"
import { X } from "lucide-react"
import { Heading } from "common/components/Heading"
import { useTranslations } from "modules/i18n/hooks/useTranslations"

interface PersonProps {
  avatar: string
  name: string
  caption: string
}

interface DonationDialogProps {
  title: string
  body: string
  donateText: string
  person: PersonProps
  onClose: Function
  onClick: MouseEventHandler<HTMLButtonElement>
  creativeRef: RefObject<HTMLDivElement>
}

export const DonationDialog = ({
  title,
  body,
  donateText,
  person,
  onClose,
  onClick,
  creativeRef,
}: DonationDialogProps) => {
  const [open, setOpen] = useState(true)
  const modalRef = useRef<HTMLDivElement>(null)
  const t = useTranslations("promos")

  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", open)
  }, [open])

  const handleClose = () => {
    setOpen(false)
    onClose()
  }

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as HTMLElement)) {
      handleClose()
    }
  }

  if (!open) {
    return null
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      data-testid="backdrop"
      onClick={handleBackdropClick}
    >
      <section
        className="mt-16 flex min-h-full items-start justify-center p-4 text-center text-white sm:mt-0 sm:items-center sm:p-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={creativeRef}
        data-testid="donation-dialog"
      >
        <div
          className="relative mt-8 overflow-y-visible rounded-md bg-red-500 px-6 pt-5 pb-4 shadow-xl sm:my-16 sm:w-full sm:max-w-lg sm:p-8"
          ref={modalRef}
          data-testid="modal"
        >
          {/* Avatar */}
          <div className="absolute inset-x-0 -top-8 mx-auto w-16">
            {/* We're leaving the alt empty, as avatar is for decorative purposes only */}
            <img src={person.avatar} alt="" className="rounded-full" />
          </div>

          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              role="button"
              aria-label={t("button.close")}
              onClick={handleClose}
            >
              <X className="size-6" />
            </button>
          </div>

          <div className="mt-3 sm:mt-0">
            <div id="modal-title" className="mt-12">
              <Heading level={4}>{title}</Heading>
            </div>

            <div className="mt-2">
              <p>{body}</p>
            </div>

            <hr className="mx-auto my-4 h-px w-5 border-none bg-white" />

            <p>{person.name}</p>
            <p className="text-xs leading-3">{person.caption}</p>
          </div>

          <div className="mt-4 mb-8">
            <button
              className="mt-3 inline-flex rounded bg-white px-10 py-2 font-medium text-black hover:bg-gray-50"
              onClick={onClick}
            >
              {donateText}
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
